/**
 * ForecastDrawer Component
 * Update forecast at completion for a line item
 */

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useForm, Controller } from 'react-hook-form';
import { X, TrendingUp } from 'lucide-react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormTextArea, CurrencyInput } from '../forms';
import type { BudgetLineItem, UpdateForecastRequest } from '../../types/budget';

export interface ForecastDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: UpdateForecastRequest) => Promise<void>;
  lineItem: BudgetLineItem | null;
  isLoading?: boolean;
}

// Form data type
interface ForecastFormData {
  forecastAtCompletion: number;
  estimateToComplete: number;
  notes?: string;
}

// Validation schema
const forecastSchema: Yup.ObjectSchema<ForecastFormData> = Yup.object({
  forecastAtCompletion: Yup.number()
    .required('Forecast at completion is required')
    .min(0, 'Forecast must be positive'),
  estimateToComplete: Yup.number()
    .required('Estimate to complete is required')
    .min(0, 'Estimate must be positive'),
  notes: Yup.string().optional(),
}) as Yup.ObjectSchema<ForecastFormData>;

const ForecastDrawer: React.FC<ForecastDrawerProps> = ({
  isOpen,
  onClose,
  onSubmit,
  lineItem,
  isLoading = false,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<ForecastFormData>({
    resolver: yupResolver(forecastSchema) as any,
    defaultValues: {
      forecastAtCompletion: 0,
      estimateToComplete: 0,
      notes: '',
    },
  });

  const forecastAtCompletion = watch('forecastAtCompletion');
  const _estimateToComplete = watch('estimateToComplete');

  // Calculate variance from forecast
  const forecastVariance = lineItem ? lineItem.revisedBudget - forecastAtCompletion : 0;
  const forecastVariancePercent =
    lineItem && lineItem.revisedBudget > 0 ? (forecastVariance / lineItem.revisedBudget) * 100 : 0;

  useEffect(() => {
    if (lineItem) {
      reset({
        forecastAtCompletion: lineItem.forecastAtCompletion,
        estimateToComplete: lineItem.estimateToComplete,
        notes: '',
      });
    }
  }, [lineItem, reset]);

  const handleFormSubmit = async (data: ForecastFormData) => {
    if (!lineItem) return;

    try {
      await onSubmit({
        lineItemId: lineItem._id,
        forecastAtCompletion: data.forecastAtCompletion,
        estimateToComplete: data.estimateToComplete,
        notes: data.notes,
      });
      onClose();
      reset();
    } catch (error) {
      console.error('Error updating forecast:', error);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  if (!isOpen || !lineItem) return null;

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90] animate-in fade-in duration-300"
        onClick={handleClose}
      />

      {/* Drawer - Small size */}
      <div className="fixed top-6 right-6 bottom-6 z-[100] w-[60%] animate-in slide-in-from-right duration-300">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 h-full flex flex-col overflow-hidden brand-form">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200 bg-white rounded-t-2xl flex-shrink-0">
            <div className="flex items-center space-x-3">
              <TrendingUp className="h-5 w-5 text-brand-600" />
              <h2 className="text-lg font-semibold text-gray-900">Update Forecast</h2>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="rounded-lg p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit(handleFormSubmit)} className="flex-1 overflow-y-auto">
            <div className="px-8 py-6 space-y-6">
              {/* Line Item Info */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900">
                      {lineItem.costCode} - {lineItem.description}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Revised Budget:</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {formatCurrency(lineItem.revisedBudget)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Actual:</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {formatCurrency(lineItem.actual)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Current Forecast */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">Current Forecast</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-blue-700">At Completion:</span>
                    <span className="ml-2 font-medium text-blue-900">
                      {formatCurrency(lineItem.forecastAtCompletion)}
                    </span>
                  </div>
                  <div>
                    <span className="text-blue-700">To Complete:</span>
                    <span className="ml-2 font-medium text-blue-900">
                      {formatCurrency(lineItem.estimateToComplete)}
                    </span>
                  </div>
                </div>
              </div>

              {/* New Forecast Inputs */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-gray-900">New Forecast</h4>

                <Controller
                  name="forecastAtCompletion"
                  control={control}
                  render={({ field }) => (
                    <CurrencyInput
                      label="Forecast at Completion"
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.forecastAtCompletion?.message}
                      required
                      placeholder="$0.00"
                    />
                  )}
                />

                <Controller
                  name="estimateToComplete"
                  control={control}
                  render={({ field }) => (
                    <CurrencyInput
                      label="Estimate to Complete"
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.estimateToComplete?.message}
                      required
                      placeholder="$0.00"
                    />
                  )}
                />
              </div>

              {/* Impact Analysis */}
              {forecastAtCompletion > 0 && (
                <div
                  className={`border rounded-lg p-4 ${
                    forecastVariance >= 0
                      ? 'bg-green-50 border-green-200'
                      : 'bg-red-50 border-red-200'
                  }`}
                >
                  <h4
                    className={`text-sm font-semibold mb-2 ${
                      forecastVariance >= 0 ? 'text-green-900' : 'text-red-900'
                    }`}
                  >
                    Forecast Impact
                  </h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex items-center justify-between">
                      <span className={forecastVariance >= 0 ? 'text-green-700' : 'text-red-700'}>
                        Variance from Budget:
                      </span>
                      <span
                        className={`font-semibold ${
                          forecastVariance >= 0 ? 'text-green-900' : 'text-red-900'
                        }`}
                      >
                        {forecastVariance >= 0 ? '+' : ''}
                        {formatCurrency(forecastVariance)} ({forecastVariancePercent.toFixed(1)}%)
                      </span>
                    </div>
                    <p
                      className={`text-xs mt-2 ${
                        forecastVariance >= 0 ? 'text-green-700' : 'text-red-700'
                      }`}
                    >
                      {forecastVariance >= 0
                        ? 'This forecast indicates the project will be under budget.'
                        : 'This forecast indicates the project will be over budget. Consider mitigation strategies.'}
                    </p>
                  </div>
                </div>
              )}

              {/* Notes */}
              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <FormTextArea
                    label="Notes"
                    {...field}
                    error={errors.notes}
                    placeholder="Add notes about this forecast update..."
                    rows={3}
                  />
                )}
              />
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 px-8 py-6 bg-gray-50 rounded-b-2xl flex-shrink-0">
              <div className="flex items-center justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? 'Updating...' : 'Update Forecast'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>,
    document.body
  );
};

export default ForecastDrawer;
